import { useMutation, useQuery } from "@tanstack/react-query"
import { isAxiosError } from "axios"
import axiosRetry from "axios-retry"
import { isStandaloneMode } from "@/utils/pwa-utils"
import { PNApiOperations } from "./operations"
import { CreditsConsumptionMetadata } from "./dto/payment"
import { store } from "@/store/store"
import { returnToAtom } from "./OAuthClient"
import { isAuthReadyAtom } from "@/store/auth-state"
import { useAtomValue } from "jotai"
import { PSApiOperations } from "./PSApiOperations"
import { PhotoEditorPlanResponseDTO } from "./dto/pixieset-payment"
import { uuid } from "@/utils/uuid"
import { isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import { isAxiosNetworkError } from "@/utils/network-error"
import { BASE_NETWORK_RETRY_DELAY_MS } from "./network-behavior"

export const PIXIESET_PAYMENT_PLANS_QUERY_KEY = ["pixieset/payment/plans"] as const

export const useServiceStatusQuery = () => {
	return useQuery({
		queryKey: ["service-status"],
		queryFn: PNApiOperations.getServiceStatus,
	})
}

export const usePaymentPlansQuery = () => {
	return useQuery({
		queryKey: ["payment/plans"],
		queryFn: PNApiOperations.getPaymentPlans,
	})
}

export const usePixiesetPaymentPlansQuery = () => {
	const isAuthReady = useAtomValue(isAuthReadyAtom)

	return useQuery<PhotoEditorPlanResponseDTO[]>({
		queryKey: PIXIESET_PAYMENT_PLANS_QUERY_KEY,
		queryFn: PSApiOperations.getPhotoEditorPlans,
		enabled: isAuthReady,
	})
}

export const useCreateCheckoutSessionMutation = (returnTo?: string) => {
	return useMutation({
		mutationKey: ["payment/stripe/checkout-sessions"],
		mutationFn: PNApiOperations.createStripeCheckoutSession,
		onSuccess: (data) => {
			if (returnTo) {
				store.set(returnToAtom, returnTo)
			}

			if (isStandaloneMode()) {
				location.href = data.url
			} else {
				window.open(data.url, "_blank")
			}
		},
	})
}

export const useOpenCustomerPortalMutation = (returnTo?: string) => {
	return useMutation({
		mutationKey: ["payment/stripe/customer-portal-sessions"],
		mutationFn: PNApiOperations.createStripeCustomerPortalSession,
		onSuccess: (data) => {
			if (returnTo) {
				store.set(returnToAtom, returnTo)
			}
			window.open(data.url, "_blank")
		},
	})
}

export const useRedeemCodeMutation = () => {
	return useMutation({
		mutationKey: ["payment/promo-codes/redemptions"],
		mutationFn: PNApiOperations.redeemPromoCode,
	})
}

export function buildExportCreditConsumptionEvent(
	photoId: string,
	metadata: CreditsConsumptionMetadata,
) {
	return {
		idempotencyKey: isPixiesetAuthEnabled() ? `${photoId}:${uuid()}` : photoId,
		metadata,
	}
}

const shouldRetryWhenPageBecomesVisible = (error: unknown) => {
	return isAxiosNetworkError(error)
}

const waitForPageToBecomeVisible = async () => {
	if (typeof document === "undefined" || document.visibilityState === "visible") {
		return
	}

	await new Promise<void>((resolve) => {
		const handleVisibilityChange = () => {
			if (document.visibilityState !== "visible") {
				return
			}

			document.removeEventListener("visibilitychange", handleVisibilityChange)
			resolve()
		}

		document.addEventListener("visibilitychange", handleVisibilityChange)

		// Re-check after listener registration so we do not miss a visibility flip
		// that happens while the listener is being attached.
		handleVisibilityChange()
	})
}

export async function consumeExportCredits(
	events: { idempotencyKey: string; metadata: CreditsConsumptionMetadata }[],
	options?: {
		retryWhenPageBecomesVisible?: boolean
	},
): Promise<void> {
	try {
		return await PNApiOperations.reportCreditConsumptionEvents({
			events: events,
		})
	} catch (error) {
		if (options?.retryWhenPageBecomesVisible && shouldRetryWhenPageBecomesVisible(error)) {
			await waitForPageToBecomeVisible()

			return PNApiOperations.reportCreditConsumptionEvents({
				events: events,
			})
		}

		// Transient downstream API errors can occur on calls to Pixieset to bootstrap user data.
		// Export credit consumption is safe to retry once because the API dedupes by idempotency key.
		if (isAxiosError(error) && [502, 503, 504].includes(error.response?.status ?? 0)) {
			// Reuse axios-retry's backoff calculation so this one-off retry matches the client default.
			await new Promise((resolve) =>
				setTimeout(
					resolve,
					axiosRetry.exponentialDelay(1, error, BASE_NETWORK_RETRY_DELAY_MS),
				),
			)

			return PNApiOperations.reportCreditConsumptionEvents({
				events: events,
			})
		}

		throw error
	}
}

export async function consumeGenAiCredit(idempotencyKey: string): Promise<void> {
	return PNApiOperations.reportGenAiCreditConsumptionEvents({
		events: [{ idempotencyKey }],
	})
}
